import React, { FC } from 'react'
import { Stack, Typography } from '@mui/material'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import type { PickersCalendarHeaderProps } from '@mui/x-date-pickers/PickersCalendarHeader'
import moment, { Moment } from 'moment-timezone'
import { useTranslation } from 'react-i18next'

import { BEIGE_600, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { MUIIconButton } from 'components/common/MUIIconButton'

const ARROW_SX = { padding: '0.5rem', borderColor: BEIGE_600 }

/**
 * Calendar header of the shooting schedule: the month in full weight beside a muted year, and the month arrows on the right.
 * An arrow is disabled once the month beyond it holds no selectable day.
 */
export const ShootingCalendarHeader: FC<PickersCalendarHeaderProps<Moment>> = ({
  currentMonth,
  onMonthChange,
  minDate,
  maxDate,
  disablePast,
  timezone,
  labelId,
}) => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  const today = timezone && timezone !== 'default' && timezone !== 'system' ? moment.tz(timezone) : moment()
  const lowest = disablePast && today.isAfter(minDate) ? today : minDate
  const isPreviousDisabled = !currentMonth.isAfter(lowest, 'month')
  const isNextDisabled = !currentMonth.isBefore(maxDate, 'month')

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.6}>
      <Typography id={labelId} aria-live="polite" variant="text-lg" fontWeight={550} color={GRAY_900}>
        {currentMonth.format('MMMM')}{' '}
        <Typography component="span" variant="text-lg" fontWeight={550} color={GRAY_700}>{currentMonth.format('YYYY')}</Typography>
      </Typography>

      <Stack direction="row" gap={0.4}>
        <MUIIconButton
          size="sm"
          icon={<ChevronLeftRoundedIcon />}
          ariaLabel={t('purchase_flow_revamp:instructions.schedule.previous_month')}
          disabled={isPreviousDisabled}
          onClick={() => onMonthChange(currentMonth.clone().subtract(1, 'month'), 'right')}
          sx={ARROW_SX}
        />
        <MUIIconButton
          size="sm"
          icon={<ChevronRightRoundedIcon />}
          ariaLabel={t('purchase_flow_revamp:instructions.schedule.next_month')}
          disabled={isNextDisabled}
          onClick={() => onMonthChange(currentMonth.clone().add(1, 'month'), 'left')}
          sx={ARROW_SX}
        />
      </Stack>
    </Stack>
  )
}
